import React from "react";
import { connect } from "react-redux";

import { bindActionCreators, Dispatch } from "redux";

import { MinusIcon, PlusIcon } from "@plex/icons";
import { SelectionMode, DataTableDef, Form } from "@plex/react-components";

import { InlineEdit } from "ui/components";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers";

import { releaseMessages } from "../../release.localization";
import { releaseDetailsManagerActions } from "../ReleaseDetails.actions";
import styles from "../ReleaseDetails.module.scss";
import { IReleaseDetailsState, ITermData, Status } from "../ReleaseDetails.state";

export interface IReleaseTermsGridProps {
  selectedAssociatedTerms: ITermData[];
  associatedTerms: ITermData[];
  status: string;
  selectAssociatedTerm: (termId: ITermData[]) => void;
}

const ReleaseTermsGrid: React.FC<IReleaseTermsGridProps> = ({ selectedAssociatedTerms, associatedTerms, status, selectAssociatedTerm }) => {
  const localizedValues = useLocalizedValues(() => ({
    termTitle: displayMessages.property("term"),
    descriptionTitle: displayMessages.property("description"),
    termsSectionTitle: displayMessages.heading("associatedTerms"),
    status: displayMessages.property("status")
  }));

  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const isReadOnlyMode = !roles.PlexTechnicalWriter || status === releaseMessages.option("releaseStatusPlanning").value;

  const renderStatus = (status: Status) => {
    switch (status) {
      case Status.Added:
        return (
          <div className={styles.iconContainer}>
            <PlusIcon className={styles.plusIcon} />
          </div>
        );
      case Status.Deleted:
        return (
          <div className={styles.iconContainer}>
            <MinusIcon className={styles.minusIcon} />
          </div>
        );
      default:
        return <div></div>;
    }
  };

  return (
    <Form.Row>
      <DataTableDef<ITermData>
        selected={selectedAssociatedTerms}
        keySelector={row => row.term.termId}
        selectionMode={SelectionMode.multiple}
        data={associatedTerms}
        onSelectionChanged={selectAssociatedTerm}
        className={styles.base}
        canSelectRow={() => isReadOnlyMode}
      >
        <DataTableDef.Column<ITermData> id="term" key="1" sortable shortTitle={localizedValues.termTitle} valueSelector={row => row.term.term}>
          {row => (
            <InlineEdit<ITermData>
              initialValue={row.term.term}
              originalValue={row.term.term}
              className={styles.inlineEdit}
              context={row}
              canEdit={() => false}
              onValueChanged={() => {}}
              validationIssues={row.validationIssues}
            >
              {row.term.term}
            </InlineEdit>
          )}
        </DataTableDef.Column>
        <DataTableDef.Column<ITermData> id="description" key="2" sortable shortTitle={localizedValues.descriptionTitle} valueSelector={row => row.term.definition}>
          {row => row.term.definition}
        </DataTableDef.Column>
        <DataTableDef.Column<ITermData> id="status" key="3" sortable shortTitle={localizedValues.status} valueSelector={row => row.status} className={styles.columnStatus}>
          {row => renderStatus(row.status)}
        </DataTableDef.Column>
      </DataTableDef>
    </Form.Row>
  );
};

export default connect(
  (state: { releaseDetails: IReleaseDetailsState }) => {
    return {
      associatedTerms: state.releaseDetails.associatedTerms,
      selectedAssociatedTerms: state.releaseDetails.selectedAssociatedTerms,
      status: state.releaseDetails.releaseStatus
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        selectAssociatedTerm: releaseDetailsManagerActions.selectAssociatedTerm
      },
      dispatch
    )
)(ReleaseTermsGrid);
